import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarIcon,
  ClockIcon,
  BellIcon,
  TrashIcon,
  DocumentArrowUpIcon,
  CurrencyEuroIcon,
  MapPinIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import HistoryModal from './modals/HistoryModal';
import NotificationsModal from './modals/NotificationModal';
import ExpensesModal from './modals/ExpensesModal';
import AppointmentModal from './modals/AppointmentModal';
import DeleteConfirmationModal from './modals/DeleteConfirmationModal';
import ParkingModal from './modals/ParkingModal';
import CarDetailsModal from './modals/CarDetailsModal';
import axios from 'axios';
import { auth } from '../../firebase';

const QuickActionPanel = ({ selectedCar, onCarsChanged }) => {
  const { t } = useTranslation();
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);
  const [isExpensesModalOpen, setIsExpensesModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isParkingModalOpen, setIsParkingModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Add effect to prevent scrolling when modal is open
  useEffect(() => {
    if (isAppointmentModalOpen || isHistoryModalOpen || isNotificationsModalOpen || isExpensesModalOpen || isDeleteModalOpen || isParkingModalOpen || isDetailsModalOpen) {
      // Prevent scrolling on the body when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = 'auto';
    }

    // Cleanup function to ensure we restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isAppointmentModalOpen, isHistoryModalOpen, isNotificationsModalOpen, isExpensesModalOpen, isDeleteModalOpen, isParkingModalOpen, isDetailsModalOpen]);

  // Action handler with car ID
  const handleAction = async (action) => {
    if (!selectedCar) {
      alert(t('quickActions.selectCarFirst'));
      return;
    }

    console.log(`Action: ${action} for car ID: ${selectedCar.id}`);

    switch (action) {
      case 'programare':
        setIsAppointmentModalOpen(true);
        break;
      case 'istoric':
        setIsHistoryModalOpen(true);
        break;
      case 'notificari':
        setIsNotificationsModalOpen(true);
        break;
      case 'delete':
        setIsDeleteModalOpen(true);
        break;
      case 'cheltuiala':
        setIsExpensesModalOpen(true);
        break;
      case 'parking':
        setIsParkingModalOpen(true);
        break;
      case 'detalii':
        setIsDetailsModalOpen(true);
        break;
      default:
        break;
    }
  };

  // Handle appointment completion
  const handleAppointmentComplete = (appointmentDetails) => {
    console.log('Appointment booked:', appointmentDetails);

    // Show confirmation
    alert(t('quickActions.appointmentBooked', {
      make: selectedCar.make,
      model: selectedCar.model,
      plate: selectedCar.plate,
      service: appointmentDetails.service,
      shop: appointmentDetails.shop.brandName,
      date: new Date(appointmentDetails.slot.slotStart).toLocaleDateString('ro-RO'),
      startTime: new Date(appointmentDetails.slot.slotStart).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }),
      endTime: new Date(appointmentDetails.slot.slotEnd).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })
    }));
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!selectedCar) return;

    setIsDeleting(true);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const response = await axios.delete(
        `https://api.servicegest.ro/driver/car/delete?registrationNumber=${encodeURIComponent(selectedCar.plate)}`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      if (response.status === 200) {
        alert(t('quickActions.deleteSuccess', {
          make: selectedCar.make,
          model: selectedCar.model,
          plate: selectedCar.plate
        }));
        setIsDeleteModalOpen(false);
        if (onCarsChanged) onCarsChanged(); // Refresh car list
      } else {
        alert(t('quickActions.deleteError'));
      }
    } catch (error) {
      alert(t('quickActions.deleteError'));
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Action items with updated styling
  const actionItems = [
    {
      name: t('common.details'),
      icon: InformationCircleIcon,
      onClick: () => handleAction('detalii'),
      color: 'slate'
    },
    // { 
    //   name: t('quickActions.expenses'), 
    //   icon: CurrencyEuroIcon, 
    //   onClick: () => handleAction('cheltuiala'),
    //   color: 'purple'
    // },
    {
      name: t('quickActions.appointment'),
      icon: CalendarIcon,
      onClick: () => handleAction('programare'),
      color: 'blue'
    },
    {
      name: t('quickActions.parking'),
      icon: MapPinIcon,
      onClick: () => handleAction('parking'),
      color: 'cyan'
    },
    {
      name: t('quickActions.notifications'),
      icon: BellIcon,
      onClick: () => handleAction('notificari'),
      color: 'green'
    },
    {
      name: t('quickActions.history'),
      icon: ClockIcon,
      onClick: () => handleAction('istoric'),
      color: 'indigo'
    },
    {
      name: t('quickActions.delete'),
      icon: TrashIcon,
      onClick: () => handleAction('delete'),
      color: 'red'
    },
  ];

  // Color mappings for each action
  const getColorClasses = (color, isDisabled) => {
    if (isDisabled) {
      return {
        container: 'bg-slate-100/95 dark:bg-slate-900/50 border-slate-200/80 dark:border-slate-700/50',
        icon: 'text-slate-400 dark:text-slate-500',
        text: 'text-slate-400 dark:text-slate-500'
      };
    }

    const colors = {
      slate: {
        container: 'bg-slate-50/95 dark:bg-slate-900/30 border-slate-200/80 dark:border-slate-700/40 hover:bg-slate-100/95 dark:hover:bg-slate-900/40 hover:border-slate-300/80 dark:hover:border-slate-600/50 hover:shadow-lg',
        icon: 'text-slate-600 dark:text-slate-400',
        text: 'text-slate-700 dark:text-slate-300'
      },
      purple: {
        container: 'bg-purple-50/95 dark:bg-purple-900/30 border-purple-200/80 dark:border-purple-700/40 hover:bg-purple-100/95 dark:hover:bg-purple-900/40 hover:border-purple-300/80 dark:hover:border-purple-600/50 hover:shadow-lg',
        icon: 'text-purple-600 dark:text-purple-400',
        text: 'text-purple-700 dark:text-purple-300'
      },
      blue: {
        container: 'bg-blue-50/95 dark:bg-blue-900/30 border-blue-200/80 dark:border-blue-700/40 hover:bg-blue-100/95 dark:hover:bg-blue-900/40 hover:border-blue-300/80 dark:hover:border-blue-600/50 hover:shadow-lg',
        icon: 'text-blue-600 dark:text-blue-400',
        text: 'text-blue-700 dark:text-blue-300'
      },
      cyan: {
        container: 'bg-cyan-50/95 dark:bg-cyan-900/30 border-cyan-200/80 dark:border-cyan-700/40 hover:bg-cyan-100/95 dark:hover:bg-cyan-900/40 hover:border-cyan-300/80 dark:hover:border-cyan-600/50 hover:shadow-lg',
        icon: 'text-cyan-600 dark:text-cyan-400',
        text: 'text-cyan-700 dark:text-cyan-300'
      },
      green: {
        container: 'bg-emerald-50/95 dark:bg-emerald-900/30 border-emerald-200/80 dark:border-emerald-700/40 hover:bg-emerald-100/95 dark:hover:bg-emerald-900/40 hover:border-emerald-300/80 dark:hover:border-emerald-600/50 hover:shadow-lg',
        icon: 'text-emerald-600 dark:text-emerald-400',
        text: 'text-emerald-700 dark:text-emerald-300'
      },
      indigo: {
        container: 'bg-purple-50/95 dark:bg-purple-900/30 border-purple-200/80 dark:border-purple-700/40 hover:bg-purple-100/95 dark:hover:bg-purple-900/40 hover:border-purple-300/80 dark:hover:border-purple-600/50 hover:shadow-lg',
        icon: 'text-purple-600 dark:text-purple-400',
        text: 'text-purple-700 dark:text-purple-300'
      },
      red: {
        container: 'bg-rose-50/95 dark:bg-rose-900/30 border-rose-200/80 dark:border-rose-700/40 hover:bg-rose-100/95 dark:hover:bg-rose-900/40 hover:border-rose-300/80 dark:hover:border-rose-600/50 hover:shadow-lg',
        icon: 'text-rose-600 dark:text-rose-400',
        text: 'text-rose-700 dark:text-rose-300'
      }
    };

    return colors[color] || colors.purple;
  };

  const isDisabled = !selectedCar;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 mb-6">
      {/* Header */}
      <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <DocumentArrowUpIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                {t('quickActions.title')}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {selectedCar ? `${selectedCar.make} ${selectedCar.model} - ${selectedCar.plate}` : 'Selectează un vehicul'}
              </p>
            </div>
          </div>
          {/* {selectedCar && (
            <div className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 rounded-full">
              <span className="text-xs font-medium text-purple-700 dark:text-purple-300">
                Se
              </span>
            </div>
          )} */}
        </div>
      </div>

      {/* Actions Grid */}
      <div className="p-2 sm:p-2">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 lg:gap-2">
          {actionItems.map((item, index) => {
            const colorClasses = getColorClasses(item.color, isDisabled);
            return (
              <button
                key={index}
                onClick={item.onClick}
                disabled={isDisabled}
                className={`
                  flex flex-col items-center justify-center p-3 sm:p-4 lg:p-2 rounded-xl border-2 min-h-[80px] sm:min-h-[100px] lg:min-h-[70px]
                  ${colorClasses.container}
                  ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-md transform hover:scale-105'}
                  
                `}
                title={item.name}
                aria-label={item.name}
              >
                <div className="mb-2 sm:mb-3 lg:mb-1">
                  <item.icon className={`h-6 w-6 sm:h-8 sm:w-8 lg:h-6 lg:w-6 ${colorClasses.icon}`} />
                </div>
                <span className={`text-xs sm:text-sm lg:text-xs font-medium text-center leading-tight ${colorClasses.text}`}>
                  {item.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Modals */}
      <AppointmentModal
        isOpen={isAppointmentModalOpen}
        onClose={() => setIsAppointmentModalOpen(false)}
        selectedCar={selectedCar}
        onAppointmentComplete={handleAppointmentComplete}
      />

      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        selectedCar={selectedCar}
      />

      <NotificationsModal
        isOpen={isNotificationsModalOpen}
        onClose={() => setIsNotificationsModalOpen(false)}
        selectedCar={selectedCar}
      />

      <ExpensesModal
        isOpen={isExpensesModalOpen}
        onClose={() => setIsExpensesModalOpen(false)}
        selectedCar={selectedCar}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        selectedCar={selectedCar}
        isDeleting={isDeleting}
      />

      <ParkingModal
        isOpen={isParkingModalOpen}
        onClose={() => setIsParkingModalOpen(false)}
        selectedCar={selectedCar}
      />

      <CarDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        selectedCar={selectedCar}
        onCarsChanged={onCarsChanged}
      />
    </div>
  );
};

export default QuickActionPanel;
